import { useContext, useEffect, useState } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { useRouter } from 'next/router';

import AppLayout from '@layouts/AppLayout';

import { StateContext } from '@store/store';
import HeaderWithCTA from '@components/header-with-cta';
import SearchPageHeader from '@components/search-page-header';
import BackButton from '@components/back-button';
import { fetchBlogList } from '@lib/API/blogAPI';
import BlogSearchCard from '@components/blog-posts/blog-search-card';
import ShowMore from '@components/showmore-button';
import NoResultFound from '@components/no-result-found';
import { Pagination } from '@constants/common';
import SEO from '@components/SEO/seo';
import { ROUTES } from '@constants/common';

import { BlogCardProps } from 'src/types';
import { Analytics } from '@invideoio/web-shared/utils/analytics';
import { AnalyticsType } from '@invideoio/web-shared/types/Analytics';
import Spinner from '@components/common/Spinner';
import {
  sendAmplitudeAnalyticEvent,
  AmplitudeEvents,
  sendRudderStackAnalyticEvent,
} from '@utils/analytics';
import useTranslation from 'next-translate/useTranslation';

type API_STATE = 'IDLE' | 'PENDING' | 'SUCESS' | 'FAILED';

const SearchPage = () => {
  const globalState = useContext(StateContext);
  const { platform, browser } = globalState;
  const { query } = useRouter();

  const { lang } = useTranslation();

  const language = typeof query.lang === 'string' ? query.lang : lang;

  const [searchedBlogList, setSearchedBlogList] = useState<BlogCardProps[]>([]);
  const [totalBlogCount, setTotalBlogCount] = useState<number>(0);
  const [status, setStatus] = useState<API_STATE>('IDLE');

  useEffect(() => {
    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad);
  }, []);

  async function searchBlogs({
    start,
    searchQuery,
  }: {
    start: number;
    searchQuery: string;
  }) {
    setStatus('PENDING');

    try {
      const { blogList, totalBlogCount } = await fetchBlogList({
        searchTitle: query.q?.toString(),
        start,
        limit: platform === 'mobile' ? 6 : Pagination.SEARCHED_BLOGS_PER_PAGE,
        language: language || 'EN',
      });

      setSearchedBlogList(searchedBlogList => [
        ...searchedBlogList,
        ...blogList,
      ]);
      setTotalBlogCount(totalBlogCount);

      setStatus('SUCESS');
      Analytics.send(AnalyticsType.AMPLITUDE, {
        eventName: 'Blog Search',
        eventProperties: {
          'Search Query': searchQuery,
          Language: language || 'EN',
        },
      });
      sendRudderStackAnalyticEvent({
        event_name: 'search_blog',
        event_properties: {
          language: (language || 'EN').toLowerCase(),
          search_term: searchQuery,
        },
      });
    } catch (error: any) {
      console.log(error.message);
      setStatus('FAILED');
    }
  }

  async function defaultSearchBlogs({ start }: { start: number }) {
    try {
      setStatus('PENDING');

      const { blogList, totalBlogCount } = await fetchBlogList({
        start,
        limit: Pagination.SEARCHED_BLOGS_PER_PAGE,
        language: language || 'EN',
      });
      setSearchedBlogList(searchedBlogList => [
        ...searchedBlogList,
        ...blogList,
      ]);
      setTotalBlogCount(totalBlogCount);
      setStatus('SUCESS');
    } catch (error: any) {
      console.log(error.message);
      setStatus('FAILED');
    }
  }

  useEffect(() => {
    setSearchedBlogList([]);
    if (query.q && typeof query.q === 'string') {
      searchBlogs({ start: 0, searchQuery: query.q });
    } else {
      defaultSearchBlogs({ start: 0 });
      setTotalBlogCount(0);
    }
  }, [query.q]);

  const titleQueryStr = query.q ? `- ${query.q}` : '';
  return (
    <AppLayout platform={platform}>
      <SEO
        title={`Search ${titleQueryStr} | InVideo Blog`}
        canonicalurl="https://invideo.io/blog/search/"
        description="InVideo is an online video creator for content and marketing videos. Read our blogs tolearn everything about creating professional quality videos."
      >
        <meta name="robots" content="noindex" />
      </SEO>
      <HeaderWithCTA />
      <SearchPageHeader />
      <section
        className={ivclass(
          'iv-bg-black-4',
          'iv-pt-75 md:iv-pt-[62px] sm:iv-pt-[50px]',
        )}
      >
        <div className={ivclass('iv-container iv-mx-auto')}>
          <div className={ivclass('iv-grid iv-grid-cols-12')}>
            <div
              className={ivclass(
                'iv-col-start-2 iv-col-span-10 md:iv-col-span-12 sm:iv-col-span-12 ',
                'md:iv-col-start-1',
                'iv-px-20 lg:iv-px-[0] md:iv-px-[65px]',
              )}
            >
              <div
                className={ivclass(
                  'iv-mb-35 md:iv-mb-15 sm:iv-mb-[22px]',
                  'sm:iv-flex sm:iv-justify-center',
                )}
              >
                <BackButton
                  isDark={true}
                  buttonContent="Back to blog"
                  redirectLink={ROUTES.BLOGHOMEPAGE}
                />
              </div>
            </div>
            {status === 'PENDING' && !(searchedBlogList.length > 0) && (
              <div
                className={ivclass(
                  'iv-col-start-1 iv-col-span-12 md:iv-col-span-12 sm:iv-col-span-12',
                  'iv-px-20 lg:iv-px-[0] md:iv-px-20',
                  'iv-mb-40 iv-mx-[120px]',
                  'iv-h-32',
                )}
              >
                <div className="iv-mx-auto iv-text-center">
                  <Spinner
                    color="#3399ff"
                    size={16}
                    style={{ margin: '0 20px' }}
                  />
                  <span
                    className={ivclass(
                      'iv-block',
                      'iv-my-15',
                      'iv-text-body-3xl iv-font-medium iv-text-grey-60',
                    )}
                  >
                    Fetching Articles
                  </span>
                </div>
              </div>
            )}
            {searchedBlogList.length == 0 && status !== 'PENDING' && (
              <div
                className={ivclass(
                  'iv-col-start-1 iv-col-span-12 md:iv-col-span-12 sm:iv-col-span-12 ',
                  'iv-px-20 lg:iv-px-[0] md:iv-px-20',
                  'iv-mb-[200px] md:iv-mb-100 sm:iv-mb-50',
                )}
              >
                <NoResultFound platform={platform} browser={browser} />
              </div>
            )}
            {searchedBlogList.length !== 0 && (
              <div
                className={ivclass(
                  'iv-col-start-2 iv-col-span-10 md:iv-col-span-12 sm:iv-col-span-12 ',
                  'md:iv-col-start-1',
                  'iv-px-20 lg:iv-px-[0] md:iv-px-[65px]',
                )}
              >
                <div className={ivclass('iv-mb-100')}>
                  {query && query.q && (
                    <div
                      className={ivclass(
                        'iv-text-heading-2xl iv-font-extrabold',
                        'md:iv-text-heading-m sm:iv-text-heading-xs',
                        'iv-text-grey-85',
                        'iv-mb-[90px] md:iv-mb-[65px] sm:iv-mb-50',
                        'sm:iv-text-center',
                        'sm:iv-px-[10px]',
                      )}
                    >
                      {`${totalBlogCount} articles for ‘`}
                      <span
                        className={ivclass('iv-text-grey-50')}
                      >{`${query.q}`}</span>
                      {`’`}
                    </div>
                  )}
                  <div
                    className={ivclass(
                      'iv-grid iv-grid-cols-12',
                      'iv-gap-x-40 iv-gap-y-[95px] ',
                      'lg:iv-gap-y-[73px] ',
                      'md:iv-gap-x-[0] md:iv-gap-y-[73px] ',
                      'sm:iv-gap-x-[0] sm:iv-gap-y-[0] ',
                    )}
                  >
                    {searchedBlogList.length > 0 &&
                      searchedBlogList.map(post => (
                        <div
                          key={post.id}
                          className={ivclass(
                            'iv-col-span-6 md:iv-col-span-12 sm:iv-col-span-12',
                            'sm:iv-mb-[40px]',
                          )}
                        >
                          <BlogSearchCard
                            title={post.title}
                            slug={post.slug}
                            image={post.featureImage}
                          />
                        </div>
                      ))}
                  </div>
                  <div
                    className={ivclass(
                      'xl:iv-mt-100 lg:iv-mt-100 md:iv-mt-80 sm:iv-mt-50',
                    )}
                  >
                    <ShowMore
                      isPaginationEnded={
                        searchedBlogList.length == totalBlogCount
                      }
                      showMoreAction={() =>
                        searchBlogs({
                          start: searchedBlogList.length,
                          searchQuery:
                            typeof query.q === 'string' ? query.q : '',
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </AppLayout>
  );
};

export default SearchPage;
